import moment from 'moment';
import type { AssemblySlotData } from '../types';
import { OrderStatus, PaymentMethod, PaymentStatus } from '../types';

moment.locale('ru');

export const getStatusLabel = (status: string | OrderStatus): string => {
  const statusLabels: Record<string, string> = {
    [OrderStatus.PENDING]: 'Ожидает оплаты',
    [OrderStatus.CONFIRMED]: 'Подтвержден',
    [OrderStatus.IN_PROGRESS]: 'В работе',
    [OrderStatus.READY]: 'Готов',
    [OrderStatus.IN_DELIVERY]: 'В доставке',
    [OrderStatus.DELIVERED]: 'Доставлен',
    [OrderStatus.CANCELED]: 'Отменен',
  };
  return statusLabels[status] || status;
};

export const getPaymentMethodLabel = (method?: string | PaymentMethod): string => {
  if (!method) return 'Не указано';
  const labels: Record<string, string> = {
    [PaymentMethod.ONLINE_CARD]: 'Картой онлайн',
    [PaymentMethod.SAVED_CARD]: 'Картой онлайн',
    [PaymentMethod.SBP]: 'СБП',
    [PaymentMethod.ON_RECEIPT]: 'При получении',
  };
  return labels[method] || method;
};

export const getPaymentStatusLabel = (status: string | PaymentStatus): string => {
  const statusLabels: Record<string, string> = {
    [PaymentStatus.PENDING]: 'Ожидает оплаты',
    [PaymentStatus.PAID]: 'Оплачен',
    [PaymentStatus.FAILED]: 'Не оплачен',
    [PaymentStatus.REFUNDED]: 'Возврат',
  };
  return statusLabels[status] || status;
};

export const formatTimeSlot = (assemblySlotData: AssemblySlotData | null | undefined): string => {
  if (!assemblySlotData) return '-';
  const { date, slotTime } = assemblySlotData;
  if (!slotTime) return '-';

  const m = moment(date);
  if (m.isSame(moment(), 'day')) {
    return slotTime;
  }
  const formattedDate = m.year() === moment().year() ? m.format('DD.MM') : m.format('DD.MM.YYYY');
  return `${formattedDate} ${slotTime}`;
};

type TzWallParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function partsInTimeZone(date: Date, timeZone: string): TzWallParts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const map: Record<string, string> = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
  };
}

/** Стена часов date+HH:mm в поясе точки → UTC ms. */
function wallTimeInTimeZoneToUtcMs(
  dateStr: string,
  hhmm: string,
  timeZone: string,
): number | null {
  const cleaned = hhmm.replace('–', '-').trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(cleaned);
  if (!match) return null;
  const year = Number(dateStr.slice(0, 4));
  const month = Number(dateStr.slice(5, 7));
  const day = Number(dateStr.slice(8, 10));
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (![year, month, day, hour, minute].every((n) => Number.isFinite(n))) {
    return null;
  }

  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 3; i += 1) {
    const p = partsInTimeZone(new Date(utcMs), timeZone);
    const got = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0);
    const want = Date.UTC(year, month - 1, day, hour, minute, 0);
    utcMs += want - got;
  }
  return utcMs;
}

/**
 * Красный: до начала слота < 2 ч либо мы уже внутри слота.
 * slotTime — часы в поясе точки; сравниваем с «сейчас» в том же поясе (через UTC).
 */
export const isSlotStartWithinTwoHours = (
  assemblySlotData: AssemblySlotData | null | undefined,
  timeZone?: string | null,
): boolean => {
  if (!assemblySlotData?.date || !assemblySlotData?.slotTime) return false;
  const parts = assemblySlotData.slotTime.split(/[-–]/).map((s) => s.trim());
  const startPart = parts[0];
  const endPart = parts[1];
  if (!startPart) return false;

  const tz =
    timeZone && typeof timeZone === 'string' && timeZone.includes('/')
      ? timeZone
      : 'Europe/Moscow';

  const startMs = wallTimeInTimeZoneToUtcMs(
    assemblySlotData.date,
    startPart,
    tz,
  );
  if (startMs == null) return false;

  const now = Date.now();
  if (now >= startMs) {
    if (!endPart) return true;
    const endMs = wallTimeInTimeZoneToUtcMs(
      assemblySlotData.date,
      endPart,
      tz,
    );
    return endMs == null || now <= endMs;
  }
  return startMs - now < 2 * 60 * 60 * 1000;
};
